/**
 * TanStack Query hook for game categories
 * 
 * Replaces the useState/useEffect pattern in useCategories.ts with cached queries.
 * Categories rarely change (admin adds them), so we cache for 60 minutes.
 * 
 * OPTIMIZATION: This query now uses the unified /lobby/init endpoint via useLobbyInitQuery
 * which fetches categories, providers, live-categories, and jackpot data in a single request.
 * Filtering by section/excludePlaythrough happens client-side to ensure single cached API call.
 */

import { useMemo } from 'react';
import { useLobbyInitQuery } from './useLobbyInitQuery';

export interface Category {
  id: number;
  name: string;
  slug: string;
  description?: string;
  image_url?: string;
  display_order: number;
  game_count?: number;
  section?: string;
  reduced_redeemable?: boolean;
}

/**
 * Fetch game categories with caching.
 * Optionally filter by section ('casino' or 'high_play').
 * Pass excludePlaythrough=true to hide categories with custom playthrough (for lobby/endless-reels).
 * 
 * OPTIMIZATION: Uses the unified /lobby/init endpoint via useLobbyInitQuery to reduce API calls.
 * Filtering happens client-side so all callers share the same cached response.
 */
export function useCategoriesQuery(section?: string, excludePlaythrough?: boolean) {
  const { data, isLoading, error, refetch } = useLobbyInitQuery();
  
  const filteredCategories = useMemo(() => {
    let categories = data?.categories || [];
    
    if (section) {
      categories = categories.filter(c => c.section === section);
    }
    if (excludePlaythrough) {
      categories = categories.filter(c => !c.reduced_redeemable);
    }
    
    return categories;
  }, [data?.categories, section, excludePlaythrough]);
  
  return {
    data: filteredCategories,
    isLoading,
    error,
    refetch,
  };
}
